import cogoToast from 'cogo-toast';
import { useDispatch, useSelector } from 'react-redux';
import { addToCart, deleteFromCart, updateQuantity, deleteAllFromCart } from '../store/slices/cart-slice';
import { addCartOrWishOne, updateCartItem, deleteAllCartOrWishItem, deleteCartOrWishItem, getCartOrWishList } from '../utils/server';
import { useNavigate } from 'react-router-dom';

export const useCartPersist = () => {
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.cartItems);
  const navigate = useNavigate();

  // 检查用户是否登录
  const checkUserLogin = () => {
    const userInfo = JSON.parse(localStorage.getItem('user_info')) || {};
    if (!userInfo.id) {
      cogoToast.error('Please login first', { position: 'top-center' });
      navigate('/login');
      return false;
    }
    return true;
  };

  // 从服务器加载购物车数据
  const loadCartFromServer = async () => {
    if (!checkUserLogin()) return;

    try {
      const { data } = await getCartOrWishList({
        flag: 1 // 1表示购物车
      });

      if (data?.list && data.list.length > 0) {
        // 清空当前购物车，然后重新添加从服务器获取的商品
        dispatch(deleteAllFromCart());

        // 逐个添加购物车商品
        data.list.forEach(item => {
          dispatch(addToCart({
            ...item,
            id: item.productId,
            productName: item.productName,
            selectedProductColor: item.color,
            selectedProductSize: item.size,
            isTip: false // 不显示提示
          }));
        });
      }
    } catch (error) {
      cogoToast.error('Failed to load cart data', { position: 'bottom-left' });
    }
  };

  // 添加商品到购物车并同步到服务器
  const addToCartWithPersist = async (product) => {
    if (!checkUserLogin()) return;

    try {
      const result = await addCartOrWishOne({
        flag: 1, // 购物车类型
        action: product.action,
        productId: product.id,
        productName: product.productName,
        productPrice: product.price,
        coverUrl: product.coverUrl,
        skuId: product.skuId || '',
        color: product.selectedProductColor,
        size: product.selectedProductSize
      });

      if (result && result.recordNo) {
        dispatch(addToCart({
          ...product,
          recordNo: result.recordNo
        }));
      } else {
        dispatch(addToCart(product));
      }

      // 添加成功后重新加载购物车数据以确保数据同步
      await loadCartFromServer();
    } catch (error) {
      cogoToast.error('Failed to add to cart', { position: 'bottom-left' });
    }
  };

  // 从购物车移除商品并同步到服务器
  const removeFromCart = async (cartItemId, recordNo) => {
    if (!checkUserLogin()) return;

    try {
      if (recordNo) {
        await deleteCartOrWishItem({
          recordNo
        });
      }

      dispatch(deleteFromCart(cartItemId));
      // 删除后重新加载购物车数据以确保数据同步
      await loadCartFromServer();
    } catch (error) {
      cogoToast.error('Failed to remove from cart', { position: 'bottom-left' });
    }
  };

  // 增加或减少购物车商品数量
  const updateCartQuantity = async (product, action) => {
    if (!checkUserLogin()) return;

    try {
      await updateCartItem({
        flag: 1, // 购物车类型
        recordNo: product.recordNo,
        action
      });

      dispatch(updateQuantity({ ...product, action }));
      // 更新后重新加载购物车数据以确保数据同步
      await loadCartFromServer();
    } catch (error) {
      const message = action > 0 ? 'Failed to increase quantity' : 'Failed to decrease quantity';
      cogoToast.error(message, { position: 'bottom-left' });
    }
  };

  // 清空购物车
  const clearCart = async () => {
    if (!checkUserLogin()) return;

    try {
      await deleteAllCartOrWishItem({
        flag: 1 // 购物车类型
      });

      dispatch(deleteAllFromCart());
      cogoToast.success('Cart cleared successfully', { position: 'bottom-left' });
    } catch (error) {
      cogoToast.error('Failed to clear cart', { position: 'bottom-left' });
    }
  };

  return {
    cartItems,
    addToCartWithPersist,
    removeFromCart,
    updateCartQuantity,
    clearCart,
    loadCartFromServer
  };
};

export default useCartPersist; 
